import AddFile from '@/common/assets/icons/addFile.svg?react';
import RightArrow from '@/common/assets/icons/arrowRightIcon.svg?react';
import ClipboardIcon from '@/common/assets/icons/clipboardIcon.svg?react';
import FileDelayIcon from '@/common/assets/icons/fileDelayIcon.svg?react';
import MyFileIcon from '@/common/assets/icons/myFile.svg?react';
import CustomIcon from '@/common/components/base/customIcon/CustomIcon.tsx';
import CustomLabel from '@/common/components/base/customLabel/CustomLabel.tsx';
import TitleDescription from '@/common/components/base/typography/TitleDescription.tsx';
import Typography from '@/common/components/base/typography/Typography.tsx';
import CardButton from '@/common/components/cardButton/CardButton.tsx';
import MainLayout from '@/common/components/layouts/MainLayout.tsx';
import useModal from '@/common/hooks/useModal.ts';
import LoadingPage from '@/common/pages/LoadingPage.tsx';
import { FileStateType } from '@/common/types/Files.type';
import StatisticIcon from '@/modules/Dashboard/assets/icons/statistic.svg?react';
import FileCard from '@/modules/Dashboard/components/fileCard/FileCard.tsx';
import HomePageAlert from '@/modules/Dashboard/components/HomePageAlerts';
import FileStatisticsCardModal from '@/modules/Dashboard/components/modals/FileStatisticsCardModal';
import ReferredModal from '@/modules/Dashboard/components/modals/ReferredModal.tsx';
import {
  generateHomePageChartData,
  homePageChartDataColor,
} from '@/modules/Dashboard/data/generateHomePageChartData.ts';
import { filesStaticsQueryOptions } from '@/modules/Dashboard/queries/fileStaticChart/filesStaticsQueryOptions.ts';
import { fileStatusCountsQueryOptions } from '@/modules/Dashboard/queries/fileStatusCounts/fileStatusCountsQueryOptions.ts';
import { homePageAlertQueryOptions } from '@/modules/Dashboard/queries/homePageAlert/homePageAlertQueryOptions';
import NewestEducation from '@/modules/Education/components/NewestEducation';
import { FILE_LIST_LINKS } from '@/modules/FileList/fileListLinks.ts';
import useOfflineFilesCheckList from '@/modules/OfflineFiles/hooks/useOfflineFilesCheckList';
import { STATISTIC_LINKS } from '@/modules/Statistics/StatisticsLinks';
import { useQuery } from '@tanstack/react-query';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { Box, Center, Flex } from 'styled-system/jsx';
import { token } from 'styled-system/tokens';
import VersionNotification from '../components/versionNotification/VersionNotification';
import { getAdjusterPerformanceScoreQueryOptions } from '../queries/getAdjusterPerformanceScore/getAdjusterPerformanceScoreQueryOptions';

ChartJS.register(ArcElement);

export default () => (
  <MainLayout header="sliderHeader" title="ارزیاب اتومبیل" navbar>
    <HomePage />
  </MainLayout>
);

const HomePage = () => {
  const navigate = useNavigate();
  useOfflineFilesCheckList();

  const { data: alertData } = useQuery(homePageAlertQueryOptions());

  const {
    data: AdjusterPerformanceScoreData,
    isPending: AdjusterPerformanceScorePending,
  } = useQuery(getAdjusterPerformanceScoreQueryOptions());

  const {
    data: fileStaticsData,
    isPending: isFileStaticsPending,
    isError: isFileStaticsError,
  } = useQuery(filesStaticsQueryOptions);
  const {
    data: fileStatusCounts,
    isPending: isFileStatusCountsPending,
    isError: isFileStatusCountError,
  } = useQuery(fileStatusCountsQueryOptions);
  const {
    open: isReferredModalOpen,
    closeModal: closeReferredModal,
    openModal: openReferredModal,
  } = useModal();

  const {
    open: isFileStatisticsCardOpen,
    closeModal: closeFileStatisticsCard,
    openModal: openFileStatisticsCard,
  } = useModal();

  const chartData = generateHomePageChartData(fileStaticsData?.counts ?? []);

  if (
    isFileStaticsPending ||
    isFileStatusCountsPending ||
    AdjusterPerformanceScorePending
  ) {
    return <LoadingPage />;
  }
  if (isFileStaticsError || isFileStatusCountError) {
    return <></>;
  }

  const referredFilesOnClick = () => {
    openReferredModal();
  };

  const inProgressCardClick = () => {
    navigate(
      FILE_LIST_LINKS.myFilesWithParams({
        state: { value: FileStateType.IN_PROGRESS, label: 'در حال اجرا' },
      }),
    );
  };

  const newFilesCardClick = () => {
    navigate(FILE_LIST_LINKS.newFiles);
  };

  const statisticClick = () => {
    navigate(STATISTIC_LINKS.statistic);
  };

  const delayCardClick = () => {
    navigate(
      FILE_LIST_LINKS.myFilesWithParams({
        state: { label: 'تاخیر خورده', value: FileStateType.DELAY },
      }),
    );
  };
  const offlineAlertCount = alertData?.alert_detail.find((item) => {
    return item.type === 'offline_shooting';
  })?.count;

  return (
    <Box paddingTop="25px" mb={'60'} scrollbar={'hidden'}>
      <Center mb="2rem" gap="1rem">
        <Box onClick={inProgressCardClick} flex={1}>
          <FileCard
            title={fileStatusCounts!.in_progress.fa_name}
            titleLabel={`${fileStatusCounts!.in_progress.count} مورد `}
            Icon={
              <CustomIcon backgroundColor="purple-6" width="48px" height="48px">
                <MyFileIcon stroke={token('colors.purple-primary')} />
              </CustomIcon>
            }
          />
        </Box>
        <Box onClick={newFilesCardClick} flex={1}>
          <FileCard
            title={fileStatusCounts!.new.fa_name}
            titleLabel={`${fileStatusCounts?.new?.count} مورد `}
            color={'green'}
            Icon={
              <CustomIcon backgroundColor="green-4" width="48px" height="48px">
                <AddFile stroke={token('colors.green-2')} />
              </CustomIcon>
            }
          />
        </Box>
      </Center>

      <Box mb={20}>
        <CardButton
          hasRate
          starData={AdjusterPerformanceScoreData?.performance_score}
          cardProps={{
            onClick: statisticClick,
          }}
          title={'آمار و عملکرد'}
          renderIcon={() => (
            <CustomIcon width="40px" height="40px" backgroundColor="orange-3">
              <StatisticIcon />
            </CustomIcon>
          )}
        />
      </Box>
      {!!offlineAlertCount ? <HomePageAlert count={offlineAlertCount} /> : null}
      <Flex flexDirection="column" gap="1rem">
        <TitleDescription color="black-1" textStyle="h2">
          نیازمند پیگیری
        </TitleDescription>
        <CardButton
          cardProps={{
            onClick: delayCardClick,
          }}
          title={fileStatusCounts!.checking_required.delay.fa_name}
          renderIcon={() => (
            <CustomIcon width="40px" height="40px" backgroundColor="orange-3">
              <FileDelayIcon stroke={token('colors.orange')} />
            </CustomIcon>
          )}
          renderTag={() => (
            <CustomLabel
              color="orange"
              title={`${fileStatusCounts!.checking_required.delay.count} مورد `}
            />
          )}
        />
        <CardButton
          cardProps={{ onClick: referredFilesOnClick }}
          title={fileStatusCounts!.checking_required.referred.fa_name}
          renderIcon={() => (
            <CustomIcon width="40px" height="40px" backgroundColor="purple-6">
              <ClipboardIcon stroke={token('colors.purple-primary')} />
            </CustomIcon>
          )}
          renderTag={() => (
            <CustomLabel
              title={`${fileStatusCounts!.checking_required.referred.count} مورد `}
            />
          )}
        />
      </Flex>

      <NewestEducation cardSize="small" />

      <ReferredModal
        closeModal={closeReferredModal}
        open={isReferredModalOpen}
      />
      <Flex
        mb="1rem"
        mt="2rem"
        alignItems="center"
        justifyContent="space-between"
      >
        <TitleDescription color="black-1" textStyle="h2">
          آمار ماه گذشته
        </TitleDescription>
        <Typography
          textStyle="b4"
          display="flex"
          alignItems="center"
          color="purple-primary"
          onClick={openFileStatisticsCard}
        >
          مشاهده جزییات
          <RightArrow stroke={token('colors.purple-primary')} width={12} />
        </Typography>
      </Flex>
      <Flex
        shadow="cardShadow"
        padding="1rem"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        backgroundColor="white"
        borderRadius="8px"
      >
        <Center width="45%" position={'relative'}>
          <Center
            flexDirection="column"
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            left={0}
          >
            <Typography textStyle="b4">{fileStaticsData!.total}</Typography>
            <Typography textStyle="b4">کل پرونده ها</Typography>
          </Center>
          <Doughnut options={{ cutout: '75%' }} data={chartData} />
        </Center>
        <Box width="50%">
          {fileStaticsData!.items.map((item, index) => (
            <Flex
              mb={index + 1 === fileStaticsData?.items.length ? '0' : '1rem'}
              key={item.name}
              justifyContent="space-between"
              gap="2rem"
            >
              <Flex alignItems="center" gap=".5rem">
                <Box
                  style={{
                    backgroundColor: token(homePageChartDataColor[index]),
                  }}
                  width="8px"
                  height="8px"
                  borderRadius="3px"
                />
                <Typography textStyle="b4">{item.fa_name}</Typography>
              </Flex>
              <Typography textStyle="b4" color="black-3">
                {item.count}
              </Typography>
            </Flex>
          ))}
        </Box>
      </Flex>

      <FileStatisticsCardModal
        closeModal={closeFileStatisticsCard}
        open={isFileStatisticsCardOpen}
      />

      <VersionNotification />
    </Box>
  );
};
